import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Building2,
  Home,
  CheckCircle2,
  AlertCircle,
  Info,
  Loader2,
  Hammer,
  Zap,
  Flame,
  Droplets,
  Wind,
  TreePine,
  Car,
  Shield,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  MapPin,
  ClipboardList,
  Lightbulb,
  BookOpen,
  Search as SearchIcon,
  Clock,
} from "lucide-react";
import { Card, Button, Badge, Input } from "@bcc/shared-ui";
import { useLocation } from "../contexts/LocationContext";
import { SEO } from "../components/SEO";
import { PERMITS_PAGE_FAQS } from "../seo/structured-data";

// Building types
const buildingTypes = [
  { id: "residential", label: "Residential", icon: Home },
  { id: "commercial", label: "Commercial", icon: Building2 },
];

// Project types by building type
const projectTypes = {
  residential: [
    { id: "new_home", label: "New Home Construction" },
    { id: "addition", label: "Addition/Extension" },
    { id: "renovation", label: "Major Renovation" },
    { id: "basement_dev", label: "Basement Development" },
    { id: "secondary_suite", label: "Secondary Suite" },
    { id: "garage", label: "Detached Garage/Accessory Building" },
    { id: "deck", label: "Deck/Patio" },
    { id: "fence", label: "Fence" },
  ],
  commercial: [
    { id: "new_building", label: "New Commercial Building" },
    { id: "tenant_improvement", label: "Tenant Improvement" },
    { id: "change_of_use", label: "Change of Use" },
    { id: "addition", label: "Building Addition" },
    { id: "renovation", label: "Major Renovation" },
    { id: "signage", label: "Signage" },
  ],
};

// Permit types with details
interface Permit {
  id: string;
  name: string;
  icon: React.ElementType;
  description: string;
  estimatedFee: string;
  processingTime: string;
  required: boolean;
  conditional?: string;
  // Extended details
  howToApply: string[];
  requiredDocuments: string[];
  applyUrl: string;
  applyAddress?: string;
  inspections?: string[];
  tips?: string[];
  codeReference?: string;
}

// Location-specific permit configuration
interface LocationPermitConfig {
  cityName: string;
  provinceName: string;
  permitPortalUrl: string;
  permitPortalAddress: string;
  safetyCodesUrl: string;
  landUseBylaw: string;
  firePermitsUrl: string;
  roadsUrl: string;
  planningUrl: string;
  contactPhone: string;
  buildingCode: string;
}

// Default config for Calgary (the only fully supported location)
const CALGARY_CONFIG: LocationPermitConfig = {
  cityName: "Calgary",
  provinceName: "Alberta",
  permitPortalUrl: "https://apply.calgary.ca/",
  permitPortalAddress:
    "Planning Services Centre, 3rd Floor, Municipal Building, 800 Macleod Trail SE",
  safetyCodesUrl: "https://www.safetycodes.ab.ca/permits-inspections/",
  landUseBylaw: "Land Use Bylaw 1P2007",
  firePermitsUrl: "https://www.calgary.ca/safety/fire-permits.html",
  roadsUrl: "https://www.calgary.ca/roads/driveway-crossing.html",
  planningUrl: "https://www.calgary.ca/planning",
  contactPhone: "311",
  buildingCode: "Alberta Building Code (NBC-AE 2023)",
};

// Determine required permits based on building specs
function determineRequiredPermits(specs: {
  buildingType: string;
  projectType: string;
  area: number;
  stories: number;
  hasPlumbing: boolean;
  hasElectrical: boolean;
  hasGas: boolean;
  hasHvac: boolean;
  hasSprinklers: boolean;
  nearProperty: boolean;
  locationConfig: LocationPermitConfig;
}): Permit[] {
  const permits: Permit[] = [];
  const config = specs.locationConfig;

  // Building Permit - almost always required
  const needsBuildingPermit =
    specs.projectType !== "fence" ||
    (specs.projectType === "fence" && specs.area > 0);
  if (needsBuildingPermit) {
    permits.push({
      id: "building",
      name: "Building Permit",
      icon: Hammer,
      description:
        "Required for construction, alteration, or demolition of buildings.",
      estimatedFee:
        specs.area > 0
          ? `$${Math.max(150, Math.round(specs.area * 12)).toLocaleString()}`
          : "$150+",
      processingTime:
        specs.buildingType === "residential"
          ? "5-10 business days"
          : "15-30 business days",
      required: true,
      howToApply: [
        `Submit application online through the ${config.cityName} ePermit portal`,
        "Prepare and upload all required documents",
        "Pay applicable fees online or at a City service centre",
        "Wait for plan review and approval",
        "Once approved, print permit and post on job site",
      ],
      requiredDocuments: [
        "Completed Building Permit Application Form",
        "Site plan showing lot dimensions, setbacks, and building footprint",
        "Floor plans with dimensions and room labels",
        "Building elevations (all sides)",
        "Cross-section drawings",
        "Structural drawings (if applicable)",
        "Energy compliance documentation (NECB/NBC 9.36)",
        "Proof of property ownership or authorization letter",
      ],
      applyUrl: config.permitPortalUrl,
      applyAddress: config.permitPortalAddress,
      inspections: [
        "Footing inspection (before pouring concrete)",
        "Foundation inspection (before backfill)",
        "Framing inspection (before insulation)",
        "Insulation/vapour barrier inspection",
        "Final inspection (before occupancy)",
      ],
      tips: [
        "Apply for Development Permit first if required - Building Permit cannot be issued without it",
        "Ensure all drawings are to scale and clearly dimensioned",
        "Consider hiring a permit expediter for complex projects",
        "Book inspections at least 24 hours in advance",
      ],
      codeReference: config.buildingCode,
    });
  }

  // Development Permit
  const needsDevPermit =
    specs.projectType === "new_home" ||
    specs.projectType === "new_building" ||
    specs.projectType === "addition" ||
    specs.projectType === "secondary_suite" ||
    specs.projectType === "change_of_use" ||
    specs.nearProperty;
  if (needsDevPermit) {
    permits.push({
      id: "development",
      name: "Development Permit",
      icon: TreePine,
      description:
        "Required when changing land use, building location, or adding structures.",
      estimatedFee:
        specs.buildingType === "residential" ? "$200-$500" : "$500-$2,000",
      processingTime: "20-40 business days",
      required: true,
      conditional: specs.nearProperty
        ? "Required due to proximity to property line"
        : undefined,
      howToApply: [
        "Check zoning and land use designation for your property",
        "Submit application through the Development Permit Portal",
        "Include all required plans and documents",
        "Pay application fee",
        "Application reviewed by Planning department",
        "May require circulation to neighbours or community association",
      ],
      requiredDocuments: [
        "Development Permit Application Form",
        "Current Certificate of Title (within 30 days)",
        "Site plan showing property lines, setbacks, and all structures",
        "Building elevations showing height and materials",
        "Landscaping plan (for commercial or multi-family)",
        "Parking and access plan",
        "Photos of existing site and adjacent properties",
      ],
      applyUrl: config.permitPortalUrl,
      applyAddress: config.permitPortalAddress,
      tips: [
        `Check ${config.cityName}'s online map to verify your property's land use district`,
        `Review the ${config.landUseBylaw} for your district's rules`,
        "Talk to neighbours early if your project needs relaxations",
        "Development Permit must be approved before Building Permit application",
        "Appeals go to the Subdivision and Development Appeal Board (SDAB)",
      ],
      codeReference: config.landUseBylaw,
    });
  }

  // Electrical Permit
  if (specs.hasElectrical) {
    permits.push({
      id: "electrical",
      name: "Electrical Permit",
      icon: Zap,
      description: "Required for any electrical installation or modification.",
      estimatedFee: "$75-$300",
      processingTime: "1-3 business days",
      required: true,
      howToApply: [
        "Must be obtained by a licensed electrical contractor OR homeowner (for own residence only)",
        "Apply online through the Alberta Safety Codes Council portal",
        "Describe scope of work and number of circuits/devices",
        "Pay permit fee based on scope",
        "Work must be inspected before energizing",
      ],
      requiredDocuments: [
        "Electrical Permit Application",
        "Contractor license number (if applicable)",
        "Description of electrical work scope",
        "Single-line diagram (for service upgrades)",
        "Load calculations (for new services or major upgrades)",
      ],
      applyUrl: config.safetyCodesUrl,
      inspections: [
        "Rough-in inspection (wiring before drywall)",
        "Final inspection (all devices installed)",
        "Service inspection (for new or upgraded services)",
      ],
      tips: [
        "Homeowners can do their own electrical work but must still get permits and inspections",
        "All electrical work must meet Canadian Electrical Code requirements",
        "Do not cover wiring until rough-in inspection is passed",
        "Keep permit posted on site during construction",
      ],
      codeReference: "Canadian Electrical Code (CEC)",
    });
  }

  // Plumbing Permit
  if (specs.hasPlumbing) {
    permits.push({
      id: "plumbing",
      name: "Plumbing Permit",
      icon: Droplets,
      description: "Required for plumbing installation, alteration, or repair.",
      estimatedFee: "$75-$250",
      processingTime: "1-3 business days",
      required: true,
      howToApply: [
        "Must be obtained by a licensed plumbing contractor OR homeowner (for own residence)",
        `Apply online through ${config.provinceName} Safety Codes portal`,
        "List all fixtures and describe work scope",
        "Pay permit fee",
        "Schedule inspections as work progresses",
      ],
      requiredDocuments: [
        "Plumbing Permit Application",
        "Contractor license number (if applicable)",
        "List of plumbing fixtures to be installed",
        "Isometric drawing (for complex installations)",
      ],
      applyUrl: config.safetyCodesUrl,
      inspections: [
        "Underground/rough-in inspection (before covering pipes)",
        "Stack test (drain waste and vent system)",
        "Final inspection (all fixtures connected)",
        "Water service inspection (for new connections)",
      ],
      tips: [
        "Ensure proper venting for all fixtures to prevent sewer gas issues",
        "Use approved materials - ABS, PVC, PEX, or copper as appropriate",
        'Maintain proper slope on drain pipes (1/4" per foot minimum)',
        `Backwater valves may be required for basement fixtures in ${config.cityName}`,
      ],
      codeReference: "National Plumbing Code (NPC 2020)",
    });
  }

  // Gas Permit
  if (specs.hasGas) {
    permits.push({
      id: "gas",
      name: "Gas Permit",
      icon: Flame,
      description: "Required for gas line installation or modification.",
      estimatedFee: "$50-$150",
      processingTime: "1-3 business days",
      required: true,
      howToApply: [
        "Must be obtained by a licensed gas contractor (no homeowner permits)",
        `Apply online through ${config.provinceName} Safety Codes portal`,
        "Describe gas appliances and BTU ratings",
        "Pay permit fee",
        "Inspection required before turning on gas",
      ],
      requiredDocuments: [
        "Gas Permit Application",
        "Gasfitter license number",
        "List of appliances with BTU ratings",
        "Gas line sizing calculations",
      ],
      applyUrl: config.safetyCodesUrl,
      inspections: [
        "Rough-in inspection (before drywall)",
        "Pressure test (to verify no leaks)",
        "Final inspection (appliances connected and vented)",
      ],
      tips: [
        "Homeowners CANNOT do their own gas work - must use licensed gasfitter",
        "Ensure adequate combustion air for all gas appliances",
        "Carbon monoxide detectors required near sleeping areas",
        "ATCO must be contacted for new gas service connections",
      ],
      codeReference: "CSA B149.1 Natural Gas and Propane Installation Code",
    });
  }

  // HVAC/Mechanical Permit
  if (specs.hasHvac) {
    permits.push({
      id: "hvac",
      name: "HVAC/Mechanical Permit",
      icon: Wind,
      description:
        "Required for heating, ventilation, and air conditioning systems.",
      estimatedFee: "$75-$200",
      processingTime: "1-3 business days",
      required: true,
      howToApply: [
        "Apply through licensed HVAC contractor or as homeowner",
        "Submit permit application online",
        "Describe equipment and ductwork scope",
        "Pay applicable fees",
        "Schedule inspections",
      ],
      requiredDocuments: [
        "HVAC Permit Application",
        "Equipment specifications (furnace, AC, HRV)",
        "Heat loss/gain calculations",
        "Ductwork layout (for new systems)",
      ],
      applyUrl: config.safetyCodesUrl,
      inspections: [
        "Rough-in inspection (ductwork before drywall)",
        "Final inspection (equipment installed and operating)",
      ],
      tips: [
        `High-efficiency furnaces (90%+) recommended for ${config.provinceName} climate`,
        "HRV (Heat Recovery Ventilator) recommended for new homes",
        "Ensure adequate return air paths from all rooms",
        "Consider right-sizing equipment - oversized units short-cycle",
      ],
      codeReference: `${config.buildingCode} Section 9.33`,
    });
  }

  // Fire Safety Permit
  if (
    specs.hasSprinklers ||
    specs.buildingType === "commercial" ||
    specs.stories > 2
  ) {
    permits.push({
      id: "fire",
      name: "Fire Safety Permit",
      icon: Shield,
      description:
        "Required for fire suppression systems and commercial buildings.",
      estimatedFee: "$100-$500",
      processingTime: "5-10 business days",
      required: specs.hasSprinklers || specs.buildingType === "commercial",
      conditional:
        !specs.hasSprinklers && specs.buildingType !== "commercial"
          ? "May be required for buildings over 2 stories"
          : undefined,
      howToApply: [
        `Submit fire safety plan to ${config.cityName} Fire Department`,
        "Apply through licensed fire protection contractor",
        "Include sprinkler system design and specifications",
        "Fire Department reviews and approves plans",
        "Inspections during and after installation",
      ],
      requiredDocuments: [
        "Fire Safety Permit Application",
        "Sprinkler system design by licensed professional",
        "Fire alarm system layout",
        "Emergency lighting and exit sign plan",
        "Fire safety plan for the building",
      ],
      applyUrl: config.firePermitsUrl,
      inspections: [
        "Underground piping inspection",
        "Rough-in inspection (before ceiling closure)",
        "Hydrostatic test",
        "Final inspection and acceptance test",
      ],
      tips: [
        "Residential sprinklers (NFPA 13D) different from commercial (NFPA 13)",
        "Fire alarm systems must be monitored 24/7",
        "Coordinate with Building Permit drawings",
        `${config.cityName} Fire may require additional measures in high-risk buildings`,
      ],
      codeReference: "National Fire Code (NFC-AE 2023), NFPA 13/13D/13R",
    });
  }

  // Parking/Driveway Permit
  if (
    specs.projectType === "new_home" ||
    specs.projectType === "new_building" ||
    specs.projectType === "garage"
  ) {
    permits.push({
      id: "parking",
      name: "Driveway/Parking Permit",
      icon: Car,
      description:
        "Required for new driveway crossings or parking lot changes.",
      estimatedFee: "$100-$300",
      processingTime: "5-10 business days",
      required: false,
      conditional: "Required if adding or modifying driveway access",
      howToApply: [
        `Apply to ${config.cityName} Roads department`,
        "Submit site plan showing driveway location",
        "Pay permit and inspection fees",
        "Driveway crossing must meet City specifications",
        "Inspection required after construction",
      ],
      requiredDocuments: [
        "Driveway Crossing Application",
        "Site plan showing driveway dimensions and location",
        "Property survey",
        "Photos of existing sidewalk/curb conditions",
      ],
      applyUrl: config.roadsUrl,
      inspections: [
        "Pre-construction inspection (verify location)",
        "Final inspection (after driveway installed)",
      ],
      tips: [
        "Maximum driveway width is typically 6.0m for residential",
        "Driveway must be at least 6m from corner",
        "Heated driveways may require additional permits",
        "Consider drainage and avoid directing water to neighbours",
      ],
      codeReference: `${config.cityName} Roads Construction Standards`,
    });
  }

  return permits;
}

export function PermitsPage() {
  const { province, city, isSupported, locationLabel } = useLocation();
  const [buildingType, setBuildingType] = useState<string>("");
  const [projectType, setProjectType] = useState<string>("");
  const [area, setArea] = useState<string>("");
  const [stories, setStories] = useState<string>("1");
  const [hasPlumbing, setHasPlumbing] = useState(false);
  const [hasElectrical, setHasElectrical] = useState(false);
  const [hasGas, setHasGas] = useState(false);
  const [hasHvac, setHasHvac] = useState(false);
  const [hasSprinklers, setHasSprinklers] = useState(false);
  const [nearProperty, setNearProperty] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [expandedPermit, setExpandedPermit] = useState<string | null>(null);

  // Build location config from context (fallback to Calgary config for unsupported locations)
  const locationConfig: LocationPermitConfig =
    isSupported && city
      ? {
          cityName: city.name,
          provinceName: province.name,
          permitPortalUrl:
            city.permitPortalUrl || CALGARY_CONFIG.permitPortalUrl,
          permitPortalAddress: CALGARY_CONFIG.permitPortalAddress, // Use Calgary's as default
          safetyCodesUrl:
            province.safetyCodesPortal || CALGARY_CONFIG.safetyCodesUrl,
          landUseBylaw: city.landUseBylaw || CALGARY_CONFIG.landUseBylaw,
          firePermitsUrl: city.firePermitsUrl || CALGARY_CONFIG.firePermitsUrl,
          roadsUrl: city.roadsUrl || CALGARY_CONFIG.roadsUrl,
          planningUrl: city.planningUrl || CALGARY_CONFIG.planningUrl,
          contactPhone: city.contactPhone || CALGARY_CONFIG.contactPhone,
          buildingCode: `${province.buildingCode} ${province.buildingCodeVersion}`,
        }
      : CALGARY_CONFIG;

  const canCheck = buildingType && projectType;

  const handleCheckPermits = () => {
    setIsAnalyzing(true);
    // Simulate analysis
    setTimeout(() => {
      setIsAnalyzing(false);
      setShowResults(true);
    }, 800);
  };

  const permits = showResults
    ? determineRequiredPermits({
        buildingType,
        projectType,
        area: parseFloat(area) || 0,
        stories: parseInt(stories) || 1,
        hasPlumbing,
        hasElectrical,
        hasGas,
        hasHvac,
        hasSprinklers,
        nearProperty,
        locationConfig,
      })
    : [];

  const requiredPermits = permits.filter((p) => p.required);
  const conditionalPermits = permits.filter((p) => !p.required);

  return (
    <>
      <SEO faqs={PERMITS_PAGE_FAQS} />
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-teal-500" />
            Permit Requirements Checker
          </h1>
          <p className="text-gray-500 mt-1">
            Enter your building specifications to find out what permits you need
            in {locationConfig.cityName}, {locationConfig.provinceName}.
          </p>
        </div>

        {/* Location Not Supported Notice */}
        {!isSupported && (
          <Card variant="outlined" className="p-4 bg-amber-50 border-amber-200">
            <div className="flex gap-3">
              <Clock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">
                  {locationLabel} - Coming Soon
                </p>
                <p>
                  Permit requirements for {locationLabel} are not yet available.
                  The information below is based on Calgary, AB data for
                  reference. We're working on adding support for more Canadian
                  cities.
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Building Specs Form */}
        <Card variant="elevated" className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Building Specifications
          </h2>

          {/* Building Type */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Building Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {buildingTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      setBuildingType(type.id);
                      setProjectType("");
                      setShowResults(false);
                    }}
                    className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                      buildingType === type.id
                        ? "border-teal-500 bg-teal-50 text-teal-700"
                        : "border-gray-200 hover:border-gray-300 text-gray-700"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                    <span className="font-medium">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Project Type */}
          {buildingType && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="mb-6"
            >
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Type
              </label>
              <div className="relative">
                <select
                  value={projectType}
                  onChange={(e) => {
                    setProjectType(e.target.value);
                    setShowResults(false);
                  }}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white
                           focus:border-teal-500 focus:ring-4 focus:ring-teal-100
                           outline-none transition-all appearance-none"
                >
                  <option value="">Select project type...</option>
                  {projectTypes[buildingType as keyof typeof projectTypes]?.map(
                    (type) => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ),
                  )}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            </motion.div>
          )}

          {/* Additional Details */}
          {projectType && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="space-y-6"
            >
              {/* Area and Stories */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Area (sq ft)
                  </label>
                  <Input
                    type="number"
                    placeholder="e.g., 1500"
                    value={area}
                    onChange={(e) => setArea(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Stories
                  </label>
                  <select
                    value={stories}
                    onChange={(e) => setStories(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 bg-white
                             focus:border-teal-500 focus:ring-4 focus:ring-teal-100
                             outline-none transition-all"
                  >
                    <option value="1">1 Story</option>
                    <option value="2">2 Stories</option>
                    <option value="3">3 Stories</option>
                    <option value="4">4+ Stories</option>
                  </select>
                </div>
              </div>

              {/* Work Includes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Work Includes (check all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    {
                      id: "electrical",
                      label: "Electrical Work",
                      state: hasElectrical,
                      setter: setHasElectrical,
                    },
                    {
                      id: "plumbing",
                      label: "Plumbing Work",
                      state: hasPlumbing,
                      setter: setHasPlumbing,
                    },
                    {
                      id: "gas",
                      label: "Gas Lines",
                      state: hasGas,
                      setter: setHasGas,
                    },
                    {
                      id: "hvac",
                      label: "HVAC System",
                      state: hasHvac,
                      setter: setHasHvac,
                    },
                    {
                      id: "sprinklers",
                      label: "Fire Sprinklers",
                      state: hasSprinklers,
                      setter: setHasSprinklers,
                    },
                    {
                      id: "property",
                      label: "Near Property Line",
                      state: nearProperty,
                      setter: setNearProperty,
                    },
                  ].map((item) => (
                    <label
                      key={item.id}
                      className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        item.state
                          ? "border-teal-500 bg-teal-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={item.state}
                        onChange={(e) => item.setter(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          item.state
                            ? "border-teal-500 bg-teal-500"
                            : "border-gray-300"
                        }`}
                      >
                        {item.state && (
                          <CheckCircle2 className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <span className="text-sm text-gray-700">
                        {item.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Check Button */}
              <Button
                onClick={handleCheckPermits}
                disabled={!canCheck || isAnalyzing}
                className="w-full bg-teal-600 hover:bg-teal-700 py-3"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Analyzing Requirements...
                  </>
                ) : (
                  <>
                    <FileText className="h-5 w-5 mr-2" />
                    Check Required Permits
                  </>
                )}
              </Button>
            </motion.div>
          )}
        </Card>

        {/* Results */}
        <AnimatePresence>
          {showResults && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {/* Summary */}
              <Card
                variant="elevated"
                className="p-6 border-l-4 border-l-teal-500"
              >
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 className="h-6 w-6 text-teal-500" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Permit Requirements Summary
                  </h3>
                </div>
                <p className="text-gray-600">
                  Based on your project specifications, you will need{" "}
                  <strong className="text-teal-700">
                    {requiredPermits.length} required permit
                    {requiredPermits.length !== 1 ? "s" : ""}
                  </strong>
                  {conditionalPermits.length > 0 && (
                    <>
                      {" "}
                      and{" "}
                      <strong className="text-amber-600">
                        {conditionalPermits.length} conditional permit
                        {conditionalPermits.length !== 1 ? "s" : ""}
                      </strong>
                    </>
                  )}
                  .
                </p>
              </Card>

              {/* Required Permits */}
              {requiredPermits.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-teal-500" />
                    Required Permits
                  </h3>
                  <div className="space-y-3">
                    {requiredPermits.map((permit) => {
                      const Icon = permit.icon;
                      const isExpanded = expandedPermit === permit.id;
                      return (
                        <Card
                          key={permit.id}
                          variant="outlined"
                          className="overflow-hidden hover:shadow-md transition-shadow"
                        >
                          <button
                            onClick={() =>
                              setExpandedPermit(isExpanded ? null : permit.id)
                            }
                            className="w-full p-4 text-left"
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                                <Icon className="h-6 w-6 text-teal-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-gray-900">
                                      {permit.name}
                                    </h4>
                                    <Badge variant="success" size="sm">
                                      Required
                                    </Badge>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-gray-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  {permit.description}
                                </p>
                                <div className="flex flex-wrap gap-4 text-sm">
                                  <span className="text-gray-500">
                                    <strong className="text-gray-900">
                                      Fee:
                                    </strong>{" "}
                                    {permit.estimatedFee}
                                  </span>
                                  <span className="text-gray-500">
                                    <strong className="text-gray-900">
                                      Processing:
                                    </strong>{" "}
                                    {permit.processingTime}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>

                          {/* Expanded Details */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="border-t border-gray-100"
                              >
                                <div className="p-4 pt-4 space-y-4 bg-gray-50">
                                  {/* How to Apply */}
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                      <ClipboardList className="h-4 w-4 text-teal-600" />
                                      How to Apply
                                    </h5>
                                    <ol className="space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
                                      {permit.howToApply.map((step, i) => (
                                        <li key={i}>{step}</li>
                                      ))}
                                    </ol>
                                  </div>

                                  {/* Required Documents */}
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                      <FileText className="h-4 w-4 text-teal-600" />
                                      Required Documents
                                    </h5>
                                    <ul className="space-y-1 text-sm text-gray-600">
                                      {permit.requiredDocuments.map(
                                        (doc, i) => (
                                          <li
                                            key={i}
                                            className="flex items-start gap-2"
                                          >
                                            <CheckCircle2 className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                                            {doc}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>

                                  {/* Inspections */}
                                  {permit.inspections &&
                                    permit.inspections.length > 0 && (
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                          <SearchIcon className="h-4 w-4 text-teal-600" />
                                          Required Inspections
                                        </h5>
                                        <ul className="space-y-1 text-sm text-gray-600">
                                          {permit.inspections.map(
                                            (inspection, i) => (
                                              <li
                                                key={i}
                                                className="flex items-start gap-2"
                                              >
                                                <span className="text-teal-600 font-medium">
                                                  {i + 1}.
                                                </span>
                                                {inspection}
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                  {/* Tips */}
                                  {permit.tips && permit.tips.length > 0 && (
                                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                                      <h5 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                                        <Lightbulb className="h-4 w-4" />
                                        Pro Tips
                                      </h5>
                                      <ul className="space-y-1.5 text-sm text-amber-700">
                                        {permit.tips.map((tip, i) => (
                                          <li
                                            key={i}
                                            className="flex items-start gap-2"
                                          >
                                            <span className="text-amber-500">
                                              •
                                            </span>
                                            {tip}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Code Reference */}
                                  {permit.codeReference && (
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <BookOpen className="h-4 w-4" />
                                      <span>
                                        Reference: {permit.codeReference}
                                      </span>
                                    </div>
                                  )}

                                  {/* Apply Button */}
                                  <div className="flex flex-wrap gap-3 pt-2">
                                    <a
                                      href={permit.applyUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                      Apply Online
                                    </a>
                                    {permit.applyAddress && (
                                      <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <MapPin className="h-4 w-4 text-gray-400" />
                                        <span>{permit.applyAddress}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Conditional Permits */}
              {conditionalPermits.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    May Be Required
                  </h3>
                  <div className="space-y-3">
                    {conditionalPermits.map((permit) => {
                      const Icon = permit.icon;
                      const isExpanded = expandedPermit === permit.id;
                      return (
                        <Card
                          key={permit.id}
                          variant="outlined"
                          className="overflow-hidden border-amber-200 bg-amber-50/50"
                        >
                          <button
                            onClick={() =>
                              setExpandedPermit(isExpanded ? null : permit.id)
                            }
                            className="w-full p-4 text-left"
                          >
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                <Icon className="h-6 w-6 text-amber-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-gray-900">
                                      {permit.name}
                                    </h4>
                                    <Badge variant="warning" size="sm">
                                      Conditional
                                    </Badge>
                                  </div>
                                  {isExpanded ? (
                                    <ChevronUp className="h-5 w-5 text-gray-400" />
                                  ) : (
                                    <ChevronDown className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  {permit.description}
                                </p>
                                {permit.conditional && (
                                  <p className="text-sm text-amber-700 bg-amber-100 px-3 py-1 rounded-lg inline-block mb-2">
                                    <Info className="h-3 w-3 inline mr-1" />
                                    {permit.conditional}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-4 text-sm">
                                  <span className="text-gray-500">
                                    <strong className="text-gray-900">
                                      Fee:
                                    </strong>{" "}
                                    {permit.estimatedFee}
                                  </span>
                                  <span className="text-gray-500">
                                    <strong className="text-gray-900">
                                      Processing:
                                    </strong>{" "}
                                    {permit.processingTime}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>

                          {/* Expanded Details */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="border-t border-amber-200"
                              >
                                <div className="p-4 pt-4 space-y-4 bg-amber-50/80">
                                  {/* How to Apply */}
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                      <ClipboardList className="h-4 w-4 text-amber-600" />
                                      How to Apply
                                    </h5>
                                    <ol className="space-y-1.5 text-sm text-gray-600 list-decimal list-inside">
                                      {permit.howToApply.map((step, i) => (
                                        <li key={i}>{step}</li>
                                      ))}
                                    </ol>
                                  </div>

                                  {/* Required Documents */}
                                  <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                      <FileText className="h-4 w-4 text-amber-600" />
                                      Required Documents
                                    </h5>
                                    <ul className="space-y-1 text-sm text-gray-600">
                                      {permit.requiredDocuments.map(
                                        (doc, i) => (
                                          <li
                                            key={i}
                                            className="flex items-start gap-2"
                                          >
                                            <CheckCircle2 className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                            {doc}
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  </div>

                                  {/* Inspections */}
                                  {permit.inspections &&
                                    permit.inspections.length > 0 && (
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                          <SearchIcon className="h-4 w-4 text-amber-600" />
                                          Required Inspections
                                        </h5>
                                        <ul className="space-y-1 text-sm text-gray-600">
                                          {permit.inspections.map(
                                            (inspection, i) => (
                                              <li
                                                key={i}
                                                className="flex items-start gap-2"
                                              >
                                                <span className="text-amber-600 font-medium">
                                                  {i + 1}.
                                                </span>
                                                {inspection}
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                  {/* Tips */}
                                  {permit.tips && permit.tips.length > 0 && (
                                    <div className="bg-white/50 rounded-lg p-3 border border-amber-200">
                                      <h5 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                                        <Lightbulb className="h-4 w-4" />
                                        Pro Tips
                                      </h5>
                                      <ul className="space-y-1.5 text-sm text-amber-700">
                                        {permit.tips.map((tip, i) => (
                                          <li
                                            key={i}
                                            className="flex items-start gap-2"
                                          >
                                            <span className="text-amber-500">
                                              •
                                            </span>
                                            {tip}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Code Reference */}
                                  {permit.codeReference && (
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <BookOpen className="h-4 w-4" />
                                      <span>
                                        Reference: {permit.codeReference}
                                      </span>
                                    </div>
                                  )}

                                  {/* Apply Button */}
                                  <div className="flex flex-wrap gap-3 pt-2">
                                    <a
                                      href={permit.applyUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                      Apply Online
                                    </a>
                                    {permit.applyAddress && (
                                      <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <MapPin className="h-4 w-4 text-gray-400" />
                                        <span>{permit.applyAddress}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <Card
                variant="outlined"
                className="p-4 bg-blue-50 border-blue-200"
              >
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Important Note</p>
                    <p>
                      This is an estimate based on typical requirements. Actual
                      permit requirements may vary based on your specific
                      location, lot conditions, and project details.
                      {isSupported ? (
                        <>
                          {" "}
                          Contact the {locationConfig.cityName} Planning
                          Services at{" "}
                          <strong>{locationConfig.contactPhone}</strong> or
                          visit{" "}
                          <a
                            href={locationConfig.planningUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-blue-900"
                          >
                            {new URL(
                              locationConfig.planningUrl,
                            ).hostname.replace("www.", "")}
                          </a>{" "}
                          for official confirmation.
                        </>
                      ) : (
                        <>
                          {" "}
                          Contact your local municipality for official
                          confirmation. The data shown is based on Calgary, AB
                          requirements.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
